import apiClient from './api';
import AuthService from './auth';
import { t } from 'i18n';

/** PKR — shipping options and free-shipping threshold (no currency conversion). */
export const PKR_FREE_SHIPPING_THRESHOLD = 15000;
export const PKR_STANDARD_SHIPPING = 2500;
export const PKR_EXPRESS_SHIPPING = 5000;

// Cart service to handle cart-related API calls
class CartService {
  /**
   * Map saved shipping amount to PKR (handles legacy USD-era values stored in localStorage).
   */
  static normalizeShippingPkr(amount) {
    const n = parseFloat(String(amount));
    if (!Number.isFinite(n)) return PKR_STANDARD_SHIPPING;
    if (n === 0 || n === 0.0) return 0;
    if (n === 10 || n === 10.0) return PKR_STANDARD_SHIPPING;
    if (n === 20 || n === 20.0) return PKR_EXPRESS_SHIPPING;
    if (n === PKR_STANDARD_SHIPPING) return PKR_STANDARD_SHIPPING;
    if (n === PKR_EXPRESS_SHIPPING) return PKR_EXPRESS_SHIPPING;
    return n;
  }
  static _resolveProductId(productId, product) {
    if (productId != null && productId !== '') return Number(productId) || productId;
    const p = product?.id ?? product?._id;
    return p != null ? (Number(p) || p) : null;
  }

  // Get user's cart items
  static async getCart() {
    if (AuthService.isAuthenticated()) {
      try {
        const response = await apiClient.get('/cart/');
        return { success: true, cartItems: response.data };
      } catch (error) {
        console.error('Get cart error:', error);
        return {
          success: false,
          error: error.response?.data?.detail || 'Failed to fetch cart'
        };
      }
    }

    try {
      const cartItems = this.loadLocalCart();
      return { success: true, cartItems };
    } catch (error) {
      console.error('Get local cart error:', error);
      return { success: false, error: 'Failed to read cart', cartItems: [] };
    }
  }

  /**
   * Daraz-style add: if the product is already in the cart, increment quantity via update API;
   * otherwise add a new line. Local (guest) cart uses immutable updates.
   */
  static async addToCart(productId, quantity = 1, product = null, modifiers = null, extra = {}) {
    // Variants: extra.variantId = a NON-default variant (the default is the
    // server's NULL line); extra.booking = a service booking (date/window/notes).
    const variantId = extra?.variantId ?? null;
    const booking = extra?.booking ?? null;
    const pid = this._resolveProductId(productId, product);
    if (pid == null) {
      return { success: false, error: 'Invalid product' };
    }

    try {
      if (AuthService.isAuthenticated()) {
        const cartRes = await this.getCart();
        if (!cartRes.success) {
          return { success: false, error: cartRes.error || 'Failed to read cart' };
        }
        // R6: a line with food modifiers is its own line -- the server merges
        // identical choices itself, so customised adds always POST.
        const customised = Boolean(modifiers && ((modifiers.remove || []).length || (modifiers.addons || []).length));
        const existing = (customised || booking) ? null : cartRes.cartItems.find(
          (item) =>
            !item.modifiers &&
            (item.variant_id ?? null) === variantId &&
            (item.product_id === pid ||
            item.product?.id === pid ||
            item.product?._id === pid)
        );
        if (existing) {
          const nextQty = (Number(existing.quantity) || 0) + (Number(quantity) || 1);
          return await this.updateCartItem(existing.id, nextQty);
        }
        const response = await apiClient.post('/cart/', {
          product_id: pid,
          quantity: Number(quantity) || 1,
          ...(customised ? { modifiers } : {}),
          ...(variantId ? { variant_id: variantId } : {}),
          ...(booking ? { booking } : {}),
        });
        return { success: true, cartItem: response.data, mode: 'added' };
      }

      // R6: the guest cart can't carry food options -- never drop them silently.
      if (modifiers && ((modifiers.remove || []).length || (modifiers.addons || []).length)) {
        return { success: false, needsLogin: true, error: t('modules.food.loginForOptions') };
      }
      if (variantId || booking) {
        return { success: false, needsLogin: true, error: t('variants.loginForOptions') };
      }
      return this.addLocalCartItem(product || { id: pid }, Number(quantity) || 1);
    } catch (error) {
      console.error('Add to cart error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to add item to cart',
      };
    }
  }

  /**
   * Add line then go straight to checkout (caller should pass navigate from useNavigate).
   */
  static async buyNow(product, navigate, quantity = 1, modifiers = null, extra = {}) {
    const pid = this._resolveProductId(product?.id ?? product?._id, product);
    if (pid == null) {
      return { success: false, error: 'Invalid product' };
    }
    try {
      const res = await this.addToCart(pid, Number(quantity) || 1, product, modifiers, extra);
      if (!res.success) return res;
      if (typeof navigate === 'function') {
        navigate('/checkout');
      }
      return { ...res, checkout: true };
    } catch (error) {
      console.error('Buy now error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Buy now failed',
      };
    }
  }

  // Update cart item quantity
  static async updateCartItem(itemId, quantity) {
    try {
      if (AuthService.isAuthenticated()) {
        const response = await apiClient.put(`/cart/${itemId}`, {
          quantity: quantity
        });
        return { success: true, cartItem: response.data, mode: 'updated' };
      }

      return this.updateLocalCartItem(itemId, quantity);
    } catch (error) {
      console.error('Update cart item error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to update cart item'
      };
    }
  }

  // Remove item from cart
  static async removeFromCart(itemId) {
    try {
      if (AuthService.isAuthenticated()) {
        await apiClient.delete(`/cart/${itemId}`);
        return { success: true };
      }

      return this.removeLocalCartItem(itemId);
    } catch (error) {
      console.error('Remove from cart error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to remove item from cart'
      };
    }
  }

  // Clear entire cart
  static async clearCart() {
    try {
      if (AuthService.isAuthenticated()) {
        const cartResult = await this.getCart();
        if (cartResult.success && cartResult.cartItems?.length) {
          const deletePromises = cartResult.cartItems.map((item) => this.removeFromCart(item.id));
          const results = await Promise.all(deletePromises);
          const failed = results.find((r) => !r.success);
          if (failed) {
            return { success: false, error: failed.error || 'Failed to clear cart' };
          }
        }
        return { success: true };
      }

      this.clearLocalCart();
      return { success: true };
    } catch (error) {
      console.error('Clear cart error:', error);
      return {
        success: false,
        error: error.message || 'Failed to clear cart'
      };
    }
  }

  static async mergeLocalCartToBackend() {
    if (!AuthService.isAuthenticated()) {
      return { success: true };
    }

    const localCart = this.loadLocalCart();
    if (!localCart.length) {
      return { success: true };
    }

    const backendResult = await this.getCart();
    if (!backendResult.success) {
      return { success: false, error: backendResult.error };
    }

    const mergePromises = localCart.map(async (localItem) => {
      const productId = localItem.product_id || localItem.id;
      const existingBackendItem = backendResult.cartItems.find(item =>
        item.product_id === productId || item.product?.id === productId
      );

      if (existingBackendItem) {
        return this.updateCartItem(existingBackendItem.id, existingBackendItem.quantity + localItem.quantity);
      }

      return this.addToCart(productId, localItem.quantity);
    });

    const mergeResults = await Promise.all(mergePromises);
    const failed = mergeResults.find(result => !result.success);
    if (failed) {
      return { success: false, error: failed.error || 'Failed to merge cart items' };
    }

    this.clearLocalCart();
    return this.getCart();
  }

  // Calculate cart totals
  // Setup standard hardcoded values for cart page display if none provided
  static calculateCartTotals(cartItems, customShippingCost = null) {
    const subtotal = cartItems.reduce((total, item) => {
      const price = parseFloat(item.unit_price ?? item.product?.price ?? 0);
      return total + (price * item.quantity);
    }, 0);

    // Calculate shipping
    let shipping = 0;
    if (customShippingCost !== null) {
      shipping = this.normalizeShippingPkr(customShippingCost);
    } else if (subtotal > 0 && subtotal < PKR_FREE_SHIPPING_THRESHOLD) {
      shipping = PKR_STANDARD_SHIPPING;
    }

    // Calculate tax (e.g., 8%). This is the loading/error fallback for the
    // Cart page -- getCheckoutQuote() supplies the server-authoritative
    // numbers once it resolves (see Cart.js).
    const tax = subtotal * 0.08;

    return {
      subtotal,
      shipping,
      tax,
      total: subtotal + shipping + tax
    };
  }

  /**
   * Server-authoritative subtotal/shipping/tax/total + per-store breakdown
   * for the Cart page (CLAUDE.md step D17b -- the grand total and every
   * per-store figure always come from here, never a client calculation).
   * Requires login; callers should only invoke this for authenticated users
   * and keep calculateCartTotals's client-side numbers as the fallback
   * while this is loading or on error.
   */
  static async getCheckoutQuote(shippingMethod = 'standard') {
    try {
      const response = await apiClient.get('/checkout/quote', {
        params: { shipping_method: shippingMethod },
      });
      const data = response.data || {};
      const subtotal = Number(data.subtotal);
      if (!Number.isFinite(subtotal)) {
        return { success: false, error: 'Invalid quote response' };
      }
      return {
        success: true,
        subtotal,
        shipping: Number(data.shipping) || 0,
        tax: Number(data.tax) || 0,
        taxRate: Number.isFinite(Number(data.tax_rate)) ? Number(data.tax_rate) : null,
        total: Number(data.total) || 0,
        stores: Array.isArray(data.stores) ? data.stores : [],
        allowedPaymentMethods: Array.isArray(data.allowed_payment_methods) ? data.allowed_payment_methods : ['cod'],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch checkout quote',
      };
    }
  }

  // Manage selected shipping method for checkout flow
  static saveShippingSelection(method, amount) {
    localStorage.setItem('selectedShippingMethod', method);
    localStorage.setItem('selectedShippingAmount', amount.toString());
  }

  static getShippingSelection() {
    const raw = localStorage.getItem('selectedShippingAmount');
    const amount = this.normalizeShippingPkr(raw ?? String(PKR_STANDARD_SHIPPING));
    return {
      method: localStorage.getItem('selectedShippingMethod') || 'standard',
      amount
    };
  }

  static clearShippingSelection() {
    localStorage.removeItem('selectedShippingMethod');
    localStorage.removeItem('selectedShippingAmount');
  }

  // Get cart item count
  static async getCartItemCount() {
    const cartResult = await this.getCart();
    if (cartResult.success) {
      return cartResult.cartItems.reduce((count, item) => count + item.quantity, 0);
    }
    return 0;
  }

  static saveLocalCart(cartItems) {
    try {
      localStorage.setItem('cart_items', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving local cart:', error);
    }
  }

  static loadLocalCart() {
    try {
      return JSON.parse(localStorage.getItem('cart_items') || '[]');
    } catch (error) {
      console.error('Error loading local cart:', error);
      return [];
    }
  }

  static clearLocalCart() {
    localStorage.removeItem('cart_items');
  }

  static addLocalCartItem(product, quantity = 1) {
    try {
      const cartItems = this.loadLocalCart();
      const pid = product?.id ?? product?._id;
      if (pid == null) {
        return { success: false, error: 'Invalid product' };
      }
      const idx = cartItems.findIndex(
        (item) => item.product_id === pid || item.product?.id === pid || item.product?._id === pid
      );

      if (idx >= 0) {
        const prev = cartItems[idx];
        const merged = {
          ...prev,
          quantity: (Number(prev.quantity) || 0) + (Number(quantity) || 1),
          product: prev.product || product,
        };
        const next = [...cartItems.slice(0, idx), merged, ...cartItems.slice(idx + 1)];
        this.saveLocalCart(next);
        return { success: true, cartItems: next, cartItem: merged, mode: 'updated' };
      }

      const newItem = {
        id: pid,
        product_id: pid,
        product: { ...product, id: pid },
        quantity: Number(quantity) || 1,
      };
      const next = [...cartItems, newItem];
      this.saveLocalCart(next);
      return { success: true, cartItems: next, cartItem: newItem, mode: 'added' };
    } catch (error) {
      console.error('Add local cart item error:', error);
      return { success: false, error: 'Failed to update cart' };
    }
  }

  static updateLocalCartItem(itemId, quantity) {
    try {
      const cartItems = this.loadLocalCart();
      const existingItem = cartItems.find(item => item.id === itemId);
      if (!existingItem) {
        return { success: false, error: 'Cart item not found' };
      }

      existingItem.quantity = quantity;
      if (existingItem.quantity < 1) {
        const filtered = cartItems.filter(item => item.id !== itemId);
        this.saveLocalCart(filtered);
        return { success: true, cartItems: filtered };
      }

      this.saveLocalCart(cartItems);
      return { success: true, cartItems };
    } catch (error) {
      console.error('Update local cart item error:', error);
      return { success: false, error: 'Failed to update cart' };
    }
  }

  static removeLocalCartItem(itemId) {
    try {
      const cartItems = this.loadLocalCart();
      const filtered = cartItems.filter(item => item.id !== itemId);
      this.saveLocalCart(filtered);
      return { success: true, cartItems: filtered };
    } catch (error) {
      console.error('Remove local cart item error:', error);
      return { success: false, error: 'Failed to remove cart item' };
    }
  }

  // Check if product is in cart
  static async isProductInCart(productId) {
    try {
      const cartResult = await this.getCart();
      if (cartResult.success) {
        return cartResult.cartItems.some(item => item.product_id === productId);
      }
      return false;
    } catch (error) {
      console.error('Check product in cart error:', error);
      return false;
    }
  }
}

export default CartService;